import { HttpContextToken, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

/**
 * Opt-out marker for cross-business requests (reporting aggregate, admin
 * oversight, and the deep-link guard's own resource-metadata fetch). A request
 * carrying `context: new HttpContext().set(BYPASS_BUSINESS_SCOPING, true)` is
 * never rewritten by the BusinessContextInterceptor.
 */
export const BYPASS_BUSINESS_SCOPING = new HttpContextToken<boolean>(() => false);

/**
 * Where the active business id is injected for a matched endpoint.
 * Path-parameter endpoints are deliberately NOT covered by the map — they are
 * handled by call-site migration in U6-U8.
 */
export interface BusinessScopeEntry {
	/** Matched against the request URL (substring for string, `.test` for RegExp). */
	urlPattern: string | RegExp;
	/** HTTP method the entry applies to. */
	method: string;
	/** Inject the active business id as a query param or into the JSON body. */
	inject: 'query' | 'body';
	/** The param/body field name the endpoint expects (e.g. `businessId`). */
	field: string;
}

/**
 * The scoping registry. Starts EMPTY — each U6/U7/U8 page-migration unit
 * enrolls its endpoints as it lands, so the interceptor only ever scopes
 * surfaces that are ready. This is what makes coverage safe during the
 * phased rollout: an un-enrolled endpoint keeps today's behavior rather than
 * being silently (mis)scoped.
 */
@Injectable({ providedIn: 'root' })
export class BusinessScopingRegistry {
	private readonly entries: BusinessScopeEntry[] = [];

	public register(entry: BusinessScopeEntry): void {
		this.entries.push(entry);
	}

	public registerAll(entries: BusinessScopeEntry[]): void {
		entries.forEach(entry => this.register(entry));
	}

	/** The first entry matching this request's method and URL, if any. */
	public match(req: HttpRequest<unknown>): BusinessScopeEntry | undefined {
		return this.entries.find(
			entry => entry.method.toUpperCase() === req.method.toUpperCase() && this.urlMatches(entry.urlPattern, req.url)
		);
	}

	private urlMatches(pattern: string | RegExp, url: string): boolean {
		return typeof pattern === 'string' ? url.indexOf(pattern) > -1 : pattern.test(url);
	}
}
